import { Component, OnInit } from '@angular/core';
import { MatDialog, MatSnackBar } from '@angular/material';
import { PopoverRef } from '@mandae/ng-popover';
import { from, Observable, of as observableOf } from 'rxjs';
import { catchError, filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { ListingBaseComponent } from '../../../dashboard/listing-base.component';
import { PerfilService } from '../../../services/ajs-perfil.service';
import { UiRouterState } from '../../../services/ajs-router-state.service';
import { StateParamsService } from '../../../services/ajs-state-params.service';
import { AddressResponseItem, AddressSearchRequest, ProfileAddressApiService } from '../../../services/api/profile-address.service';
import { TourService } from '../../../tour/tour.service';
import { AddressDefaultDialogComponent } from '../address-default-dialog/address-default-dialog.component';
import { AddressFormComponent } from '../address-form/address-form.component';
import { AddressRemoveDialogComponent } from '../address-remove-dialog/address-remove-dialog.component';

const displayColumns = [
    'address',
    'default_collect_address',
    'default_return_address',
    'default_label_address',
    'actions',
  ];

@Component({
  selector: 'mandae-address-list',
  templateUrl: './address-list.component.html',
  styleUrls: ['./address-list.component.scss']
})
export class AddressListComponent extends ListingBaseComponent<AddressSearchRequest> implements OnInit {

  public pages = 1;

  public displayedColumns = displayColumns;

  public data: AddressResponseItem[] = [];

  constructor(
      public profileAddressApiService: ProfileAddressApiService,
      private state: UiRouterState,
      private profile: PerfilService,
      private dialog: MatDialog,
      private snackBar: MatSnackBar,
      private tour: TourService,
      private stateParams: StateParamsService,
  ) {
    super();
    this.searchParameters = {
      userId: profile.getCacheUserData().id,
    };
  }

  ngOnInit() {

    super.ngOnInit();

    if (this.stateParams.tour) {
      this.startTour();
    }

    this.profileAddressApiService.getAddresses(this.searchParameters).subscribe();

    this.refresh
    .pipe(
      startWith({}),
      switchMap(() => {
        this.loading = true;
        return this.profileAddressApiService.getAddresses(this.searchParameters);
      }),
      map(data => {
        this.loading = false;

        return data.map(d => Object.assign(d, {
          coletaPadrao: d.ativo,
          devolucaoPadrao: d.enderecoDevolucao,
          etiquetaPadrao: d.etiqueta == null,
        }));
      }),
      catchError(() => {
        this.loading = false;
        return observableOf([]);
      }),
      tap(data => this.data = data))
    .subscribe();
  }

  isEmptyDatasource() {
    return !this.data || this.data.length === 0;
  }

  openAddressFormDialog() {
    const dialogRef = this.dialog.open(AddressFormComponent, {
      closeOnNavigation: true,
      width: '600px',
      maxWidth: '95vw',
      data: {},
    });

    dialogRef.componentInstance.submit.subscribe(form => {
      from(this.profileAddressApiService.addAddress({
        nome: form.value.name,
        logradouro: form.value.street,
        bairro: form.value.district,
        cep: form.value.postalCode.replace(/\D/g, ''),
        cidade: form.value.city,
        numero: form.value.number,
        complemento: form.value.complement,
        uf: form.value.state,
        remetente: form.value.sender,
        areaDedicadaColeta: form.value.collectDedicatedArea,
        coletaHabilitada: true,
        logisticaReversaHabilitada: true,
        devolucaoHabilitada: true,
      }).pipe(
        tap(data => dialogRef.close(data)),
        catchError(err => {
          return this.treatError(err);
        }),
      )).subscribe();
    });

    dialogRef
      .afterClosed()
      .pipe(
        tap(() => this.refresh.next()),
      ).subscribe();
  }

  openRemoveAddressDialog(address) {
    this.openDialog({
      type: 'coleta',
      address: address,
    },
    AddressRemoveDialogComponent,
    data => {
      this.loading = true;
      return this.profileAddressApiService.removeAddress(data);
    });
  }

  openDefaultCollectDialog(address, $event) {
    $event.preventDefault();

    this.openDialog({
      type: 'coleta',
      address: address,
    },
    AddressDefaultDialogComponent,
    data => {
      this.loading = true;
      return this.profileAddressApiService.updateAddressAsCollectDefault(data);
    });
  }

  openDefaultReturnDialog(address, $event) {
    $event.preventDefault();

    this.openDialog({
      type: 'devolução',
      address: address,
    },
    AddressDefaultDialogComponent,
    data => {
      this.loading = true;
      return this.profileAddressApiService.updateAddressAsReturnDefault(data);
    });
  }

  openDefaultLabelDialog(address, $event) {
    $event.preventDefault();

    this.openDialog({
      type: 'impressão de etiqueta',
      address: address,
    },
    AddressDefaultDialogComponent,
    data => {
      this.loading = true;
      return this.profileAddressApiService.updateAddressAsLabelDefault(data);
    });
  }

  private openDialog<R, T>(data: any, dialog: any, project: (data: any) => Observable<T> ) {
    this.dialog.open(dialog, {
      closeOnNavigation: true,
      autoFocus: false,
      width: '600px',
      maxWidth: '95vw',
      data,
    })
    .afterClosed()
    .pipe(
      filter(result => !!result),
      map(d => ({ addressId: d.id })),
      switchMap(project),
      tap(() => this.refresh.next()),
      catchError(err => this.treatError(err)))
    .subscribe();
  }

  private treatError(err: any) {

    this.loading = false;
    if (err.error && err.error.error) {
      this.snackBar.open(err.error.error.message, 'Fechar');
    } else {
      this.snackBar.open(err.message, 'Fechar');
    }

    return observableOf([]);
  }

  private startTour() {
    const steps = this.tour.makeTour([
      {
        title: 'Defina seus principais endereços',
        text: 'Inclua ou altere endereços e defina endereços de coleta, devolução e o ' +
          'que será exibido nas etiquetas de postagem das encomendas.',
        attachTarget: () => document.querySelector<HTMLElement>('app-toolbar .mat-tab-link:nth-child(3)'),
        previous: {
          text: 'Voltar',
          action: (ev, ref) => {
            ref.close('to_previous_section');
            this.state.go('contato', { tour: true });
          }
        },
        next: {
          text: 'Ir para Integrações',
          action: (ev: MouseEvent, ref: PopoverRef) => {
            ref.close('to_next_section');
            this.state.go('integration', { tour: true });
          },
        },
      },
    ]);

    this.tour.start(steps).subscribe();
  }

}
